import { useMemo } from "react";
import { parseMoodTimestamp } from "utils/common/timestampUtils";
import { useTrendsData } from "./useTrendsData";
import { useAppState } from "contexts/UserContext";
import { useCause } from "contexts/CauseContext";
import useCauseProcessor from "../../../hooks/useCauseProcessor";
import { useTrendsViewState } from "./useTrendsViewState";
import { useMoodFiltering } from "../../../shared/charts/moodBalance/useMoodFiltering";
import { useMoodAggregation } from "../../../shared/charts/moodBalance/useMoodAggregation";
import { usePieChartData } from "../../../shared/charts/moodBalance/usePieChartData";
import { getCauseTranslationOccurrences } from "utils/moods/moodAnalytics";
import { useCauseAnalysis } from "./useCauseAnalysis";
import { useWeekManagement } from "./useWeekManagement";
import { useMonthManagement } from "./useMonthManagement";
import type { MonthInfo } from "./useMonthManagement";
import { useBarChartData } from "./useBarChartData";
import { useMoodPercentages } from "./useMoodPercentages";
import { useWeekdayLabels } from "./useWeekdayLabels";
import type { Mood } from "../../../types/models";
import type { BalancePieDataItem } from "./useTrendsData";

export interface UseTrendsViewReturn {
  showCauses: boolean;
  setShowCauses: (show: boolean) => void;
  selectedView: "PERSONAL" | "TEAM";
  setSelectedView: (view: "PERSONAL" | "TEAM") => void;
  selectedCauseView: "BALANCED" | "BURNOUT" | "GROWTH" | "OVERVIEW";
  setSelectedCauseView: (
    view: "BALANCED" | "BURNOUT" | "GROWTH" | "OVERVIEW"
  ) => void;
  availablePeriods: string[];
  selectedPeriod: string;
  onPeriodChange: (period: string) => void;
  totalMoods: number;
  balancePieData: BalancePieDataItem[];
  modifiedBalancePieData: BalancePieDataItem[];
  filteredCauses: Record<string, number>;
  filteredMoods: Mood[];
  moodCounts: Record<string, number>;
  burnoutCauses: Record<string, number>;
  balancedCauses: Record<string, number>;
  topMoodsCauses: Record<string, number>;
  barChartData: Array<
    Array<{
      totalHeight: number;
      segments: Array<{
        color: string;
        height: number;
      }>;
    }>
  >;
  moodPercentages: {
    Apathy: number;
    Flow: number;
    Anxiety: number;
    Confident: number;
  };
  weekdayLabels: string[];
  lastEndedWeekPeriod: string;
  availableWeeks: Array<{ key: string; label: string; weekdays: Date[] }>;
  selectedWeek: string;
  onWeekChange: (weekKey: string) => void;
  insightsGranularity: "week" | "month";
  setInsightsGranularity: (g: "week" | "month") => void;
  availableMonths: MonthInfo[];
  selectedMonth: string;
  onMonthChange: (monthKey: string) => void;
}

export function useTrendsView(): UseTrendsViewReturn {
  const personalData = useTrendsData();
  const { moods } = useAppState();
  const { causeTypesTranslations, causes } = useCause();
  const { allCauseTypes } = useCauseProcessor();

  const {
    availablePeriods: personalAvailablePeriods,
    selectedPeriod: personalSelectedPeriod,
    onPeriodChange: personalOnPeriodChange,
    filteredMoods: personalFilteredMoods,
  } = personalData;

  const viewState = useTrendsViewState(personalSelectedPeriod);

  // For PERSONAL view, use all moods to generate weeks (not period-filtered)
  // so that weeks from all years are available
  const moodsForWeekGeneration =
    viewState.selectedView === "PERSONAL"
      ? ((moods || []) as Mood[])
      : personalFilteredMoods;

  const weekManagement = useWeekManagement(
    moodsForWeekGeneration,
    viewState.selectedWeek
  );

  const monthManagement = useMonthManagement(
    moodsForWeekGeneration,
    viewState.selectedMonth
  );

  // For PERSONAL view, filter all moods by week (not period-filtered)
  // For other views, use period-filtered moods and then filter by week
  const moodsForWeekFiltering =
    viewState.selectedView === "PERSONAL"
      ? ((moods || []) as Mood[])
      : personalFilteredMoods;

  const filteredMoodsByWeek = useMoodFiltering(
    moodsForWeekFiltering,
    weekManagement.effectiveSelectedWeek
  );

  const filteredMoodsByMonth = useMemo(() => {
    const key = monthManagement.effectiveSelectedMonth;
    if (!key) return [] as Mood[];
    const [y, mo] = key.split("-").map(Number);
    return moodsForWeekFiltering.filter((mood) => {
      const d = parseMoodTimestamp(mood);
      if (!d) return false;
      return d.getUTCFullYear() === y && d.getUTCMonth() + 1 === mo;
    });
  }, [moodsForWeekFiltering, monthManagement.effectiveSelectedMonth]);

  const filteredMoods =
    viewState.insightsGranularity === "month"
      ? filteredMoodsByMonth
      : filteredMoodsByWeek;
  // filter moods

  const { moodCounts, totalMoods } = useMoodAggregation(filteredMoods);

  const moodPercentages = useMoodPercentages(
    filteredMoods,
    viewState.selectedView,
    weekManagement.selectedWeekData,
    weekManagement.availableWeeks,
    weekManagement.lastEndedWeek,
    viewState.insightsGranularity
  );

  // Generate pie chart data

  const { balancePieData, modifiedBalancePieData } = usePieChartData(
    moodCounts,
    totalMoods,
    moodPercentages
  );

  const { filteredCauses, burnoutCauses, balancedCauses } = useCauseAnalysis(
    filteredMoods,
    causes || [],
    allCauseTypes,
    causeTypesTranslations
  );

  const topMoodNames = useMemo(
    () =>
      Object.entries(moodCounts)
        .filter(([, c]) => c > 0)
        .sort(([, a], [, b]) => (b as number) - (a as number))
        .slice(0, 2)
        .map(([name]) => name),
    [moodCounts]
  );

  const topMoodsCauses = useMemo(() => {
    const moodsForTop = filteredMoods.filter((m) =>
      topMoodNames.includes(m.mood)
    );
    return getCauseTranslationOccurrences(
      moodsForTop,
      causes || [],
      allCauseTypes,
      causeTypesTranslations
    );
  }, [
    filteredMoods,
    topMoodNames,
    causes,
    allCauseTypes,
    causeTypesTranslations,
  ]);

  const barChartData = useBarChartData(
    filteredMoods,
    viewState.selectedView,
    weekManagement.selectedWeekData,
    weekManagement.availableWeeks,
    weekManagement.lastEndedWeek,
    viewState.insightsGranularity,
    viewState.insightsGranularity === "month"
      ? monthManagement.effectiveSelectedMonth
      : ""
  );

  const weekdayLabels = useWeekdayLabels(
    viewState.selectedView,
    weekManagement.selectedWeekData,
    weekManagement.availableWeeks,
    weekManagement.lastEndedWeek,
    viewState.insightsGranularity
  );

  return {
    showCauses: viewState.showCauses,
    setShowCauses: viewState.setShowCauses,
    selectedView: viewState.selectedView,
    setSelectedView: viewState.setSelectedView,
    selectedCauseView: viewState.selectedCauseView,
    setSelectedCauseView: viewState.setSelectedCauseView,
    availablePeriods: personalAvailablePeriods,
    selectedPeriod: personalSelectedPeriod,
    onPeriodChange: personalOnPeriodChange,
    totalMoods,
    balancePieData,
    modifiedBalancePieData,
    filteredCauses,
    filteredMoods,
    moodCounts,
    burnoutCauses,
    balancedCauses,
    topMoodsCauses,
    barChartData,
    moodPercentages,
    weekdayLabels,
    lastEndedWeekPeriod: weekManagement.lastEndedWeekPeriod,
    availableWeeks: weekManagement.availableWeeks,
    selectedWeek: weekManagement.effectiveSelectedWeek,
    onWeekChange: viewState.setSelectedWeek,
    insightsGranularity: viewState.insightsGranularity,
    setInsightsGranularity: viewState.setInsightsGranularity,
    availableMonths: monthManagement.availableMonths,
    selectedMonth: monthManagement.effectiveSelectedMonth,
    onMonthChange: viewState.setSelectedMonth,
  };
}
